import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';
import { getSessionTenantId, getSessionUserId, useAuthStore } from '@shared/stores/authStore';
import type { User } from '@shared/types/auth';
import {
  FALLBACK_PROFILE_CONFIG,
  type ContactField,
  type PendingContactChange,
  type PendingProfileChange,
  type ProfileConfig,
  type ProfileSectionValues,
} from './config';

/**
 * Profile — canonical member-facing routes: `GET /public/me`,
 * `PUT /public/me`, `POST/DELETE /public/me/profile-picture`. Responses use
 * the `{ success, data }` envelope (nested `user` + `entity`, same as Angular
 * member-portal). Mutations refresh authStore so the drawer + home header stay
 * in sync.
 */

export interface MemberAccount {
  account_id: string;
  account_code: string | null;
  account_name: string | null;
  balance: number;
  currency_code: string;
  status: string;
}

/** Raw `GET /public/me` payload from Tenant API. */
export interface ApiProfileData {
  user: {
    user_id: string;
    email: string;
    is_active?: boolean;
    member_id?: string;
    profile_image_url?: string | null;
  };
  roles?: Array<{
    role_id: string;
    name: string;
    /** The portals this role opens. 'member' is the resident app. */
    audiences: string[];
  }>;
  entity: {
    type: string;
    id: string;
    data: Record<string, unknown>;
  } | null;
  account?: MemberAccount | null;
  /** Members only: what this tenant's profile is made of. */
  profile_config?: ProfileConfig;
  /** Members only: email or mobile changes awaiting a code. The live value is unchanged. */
  pending_contact_changes?: PendingContactChange[];
  /** Members only: supervised edits awaiting staff, and the last one turned down. */
  pending_profile_changes?: PendingProfileChange[];
}

export interface MemberProfile extends User {
  mobile?: string;
  date_of_birth?: string;
  gender?: string;
  id_number?: string;
  preferred_name?: string;
  profile_image_url?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  account?: MemberAccount | null;
  /** The member record's status (Pending, Active, ...). Absent for staff, who hold no member record. */
  member_status?: string;
  roles: NonNullable<ApiProfileData['roles']>;
  /** Resolved profile description; falls back to today's core fields when absent. */
  config: ProfileConfig;
  pending_contact_changes: PendingContactChange[];
  pending_profile_changes: PendingProfileChange[];
}

function pickString(data: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

/** Flatten nested profile API shape into UI-friendly member fields. */
export function mapProfileFromApi(raw: ApiProfileData): MemberProfile {
  const entityData = raw.entity?.data ?? {};
  const auth = useAuthStore.getState();

  const firstName = pickString(entityData, 'first_name', 'firstName');
  const lastName = pickString(entityData, 'last_name', 'lastName', 'surname');
  const mobile = pickString(entityData, 'mobile', 'mobile_number', 'phone');
  const profileImage =
    raw.user.profile_image_url ??
    pickString(entityData, 'profile_image_url') ??
    undefined;

  return {
    user_id: raw.user.user_id,
    tenant_id: auth.tenantId ?? auth.user?.tenant_id ?? '',
    email: raw.user.email,
    member_id: raw.user.member_id,
    permissions: auth.user?.permissions ?? [],
    firstName,
    lastName,
    preferred_name: pickString(entityData, 'preferred_name'),
    mobile,
    date_of_birth: pickString(entityData, 'date_of_birth'),
    gender: pickString(entityData, 'gender'),
    id_number: pickString(entityData, 'id_number'),
    address: {
      line1: pickString(entityData, 'address_line1'),
      line2: pickString(entityData, 'address_line2'),
      city: pickString(entityData, 'address_city'),
      region: pickString(entityData, 'address_region'),
      postal_code: pickString(entityData, 'address_postal_code'),
      country: pickString(entityData, 'address_country'),
    },
    profile_image_url: profileImage ?? undefined,
    account: raw.account ?? null,
    member_status: raw.entity?.type === 'member' ? pickString(entityData, 'status') : undefined,
    roles: raw.roles ?? [],
    config: raw.profile_config ?? FALLBACK_PROFILE_CONFIG,
    pending_contact_changes: raw.pending_contact_changes ?? [],
    pending_profile_changes: raw.pending_profile_changes ?? [],
  };
}

/** Persist member names onto authStore for header, drawer, and cold-start cache. */
export function syncProfileToAuthStore(profile: MemberProfile): void {
  useAuthStore.setState((state) => {
    const base = state.user ?? {
      user_id: profile.user_id,
      tenant_id: profile.tenant_id,
      email: profile.email,
      permissions: profile.permissions ?? [],
    };
    const user: User = {
      ...base,
      email: profile.email || base.email,
      member_id: profile.member_id ?? base.member_id,
      firstName: profile.firstName ?? base.firstName,
      lastName: profile.lastName ?? base.lastName,
      profile_image_url: profile.profile_image_url ?? base.profile_image_url ?? null,
    };
    return { user };
  });

  const { user, rememberSession } = useAuthStore.getState();
  if (user && rememberSession) localStorage.setItem('paas_user', JSON.stringify(user));
}

/** Display name for home header (preferred name, else first + last, else first / email). */
export function memberGreetingName(
  profile?: Pick<MemberProfile, 'preferred_name' | 'firstName' | 'lastName'> | null,
  user?: User | null,
): string {
  const preferred = profile?.preferred_name?.trim();
  if (preferred) return preferred;

  const first = profile?.firstName?.trim() ?? user?.firstName?.trim();
  const last = profile?.lastName?.trim() ?? user?.lastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;

  const email = user?.email?.trim();
  if (email) return email.split('@')[0] ?? 'there';
  return 'there';
}

export async function fetchProfile(): Promise<MemberProfile> {
  const sessionBefore = `${getSessionUserId()}|${getSessionTenantId()}`;

  const { data } = await tenantApi.get('/public/me');
  const profile = mapProfileFromApi(unwrap<ApiProfileData>(data));

  // The member may have signed out or switched community while the request was in flight;
  // their profile must not be written onto whoever (or nothing) is signed in now.
  const sessionNow = `${getSessionUserId()}|${getSessionTenantId()}`;
  if (sessionNow === sessionBefore && useAuthStore.getState().isAuthenticated()) {
    syncProfileToAuthStore(profile);
  }
  return profile;
}

/** Call after login / on app boot when tokens are present. */
export async function hydrateMemberProfile(): Promise<MemberProfile | null> {
  if (!useAuthStore.getState().isAuthenticated()) return null;
  try {
    return await fetchProfile();
  } catch (error) {
    console.warn('[profile] GET /public/me failed:', error);
    return null;
  }
}

/**
 * Submits one section's editable fields. The API validates them against the
 * tenant's config and rejects with errors keyed `section.field`, which the form
 * renders against the offending input.
 */
export async function updateProfile(sections: ProfileSectionValues): Promise<MemberProfile> {
  const { data } = await tenantApi.put('/public/me', { sections });
  const profile = mapProfileFromApi(unwrap<ApiProfileData>(data));
  syncProfileToAuthStore(profile);
  return profile;
}

/**
 * Asks for a code to be sent to a new email or mobile. Nothing about the live
 * value changes here, and a refusal comes back keyed `value`.
 */
export async function requestContactChange(
  field: ContactField,
  value: string,
): Promise<PendingContactChange> {
  const { data } = await tenantApi.post('/public/me/contact-change', { field, value });
  return unwrap<PendingContactChange>(data);
}

/** Confirms the change with the code sent to the new value. Errors come back keyed `code` or `value`. */
export async function verifyContactChange(field: ContactField, code: string): Promise<true> {
  await tenantApi.post('/public/me/contact-change/verify', { field, code });
  return true;
}

export async function uploadProfileImage(file: File | Blob): Promise<{ profile_image_url: string }> {
  const formData = new FormData();
  formData.append('profile_picture', file, 'profile.jpg');
  const { data } = await tenantApi.post('/public/me/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const result = unwrap<{ profile_image_url: string }>(data);
  useAuthStore.setState((state) => ({
    user: state.user ? { ...state.user, profile_image_url: result.profile_image_url } : state.user,
  }));
  return result;
}

export async function deleteProfileImage(): Promise<void> {
  await tenantApi.delete('/public/me/profile-picture');
}
